import { useState } from "react";
import useSWR from "swr";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ToastProvider";
import { post } from "@/lib/api";

const fetcher = (url: string) => fetch(url, { credentials: "include" }).then((res) => res.json());

export function RetargetingPanel() {
  const { data, isLoading, mutate } = useSWR("/marketing/ads/retarget/stats", fetcher);
  const { push } = useToast();
  const [objective, setObjective] = useState("ticket_sales");
  const [ticketCities, setTicketCities] = useState("Nashville,Denver");
  const [videoPlatforms, setVideoPlatforms] = useState("instagram,tiktok");
  const [message, setMessage] = useState("Retarget ticket viewers + video engagers.");

  const handleRetarget = async () => {
    try {
      const targets = {
        ticket_cities: ticketCities.split(",").map((city) => city.trim()),
        video_platforms: videoPlatforms.split(",").map((platform) => platform.trim()),
      };
      await post("/marketing/ads/retarget", { objective, targets, message });
      push({ title: "Retargeting queued", message: "Cohorts sent to ads platforms." });
      await mutate();
    } catch (error) {
      push({
        title: "Unable to retarget",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="space-y-3 border-border/70 bg-background/40 p-4">
      <div>
        <p className="text-sm font-semibold text-foreground">Smart Retargeting</p>
        <p className="text-xs text-muted-foreground">Ticket + merch visitors combined with video engagers.</p>
      </div>
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading audiences…</p>
      ) : (
        <div className="grid gap-2 text-xs text-muted-foreground">
          <span>Ticket visitors: {data?.ticket_visitors ?? 0}</span>
          <span>Merch visitors: {data?.merch_visitors ?? 0}</span>
          <span>Video viewers IG: {data?.video_viewers?.instagram ?? 0}</span>
          <span>Video viewers TikTok: {data?.video_viewers?.tiktok ?? 0}</span>
          <span>Engaged IG: {data?.engaged_users?.instagram ?? 0}</span>
        </div>
      )}
      <Input
        value={objective}
        onChange={(event) => setObjective(event.target.value)}
        placeholder="Objective (ticket_sales, merch, etc)"
        className="bg-background/40 text-foreground"
      />
      <Textarea
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        className="bg-background/40 text-foreground"
        rows={3}
      />
      <Input
        value={ticketCities}
        onChange={(event) => setTicketCities(event.target.value)}
        placeholder="Ticket cities (comma separated)"
        className="bg-background/40 text-foreground"
      />
      <Input
        value={videoPlatforms}
        onChange={(event) => setVideoPlatforms(event.target.value)}
        placeholder="Video platforms"
        className="bg-background/40 text-foreground"
      />
      <Button onClick={() => void handleRetarget()}>Launch retargeting</Button>
    </Card>
  );
}
